import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Copy, Trash2, Edit3, Package, Sparkles, X, Save, Users, Clock, Euro } from 'lucide-react';
import { packagesApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface CoachingPackage {
  id: string;
  name: string;
  description: string | null;
  session_count: number;
  session_duration_min: number;
  price_eur: number;
  price_per_session: number | null;
  category: string | null;
  tier: string | null;
  format: string | null;
  max_participants: number | null;
  validity_days: number | null;
  includes: string[] | null;
  is_system_template: boolean;
  sort_order: number;
  is_active: boolean;
}

const CATEGORIES = ['Leadership', 'Team', 'Konflikt-Mediation', 'Business', 'Startup', 'Personal'];
const TIERS = [
  { value: 'fokus', label: 'Fokus' },
  { value: 'transformation', label: 'Transformation' },
];
const FORMATS = [
  { value: '1:1', label: '1:1 Einzelcoaching' },
  { value: 'Gruppe', label: 'Gruppencoaching' },
  { value: 'Workshop', label: 'Workshop' },
];

const CATEGORY_COLORS: Record<string, string> = {
  'Leadership': '#B07468',
  'Team': '#6B8E7B',
  'Konflikt-Mediation': '#8B7355',
  'Business': '#5B7FA5',
  'Startup': '#9B6B9E',
  'Personal': '#A0856E',
};

const makeEmptyForm = () => ({
  name: '',
  description: '',
  session_count: 1,
  session_duration_min: 60,
  price_eur: 0,
  category: 'Leadership',
  tier: 'fokus',
  format: '1:1',
  max_participants: null as number | null,
  validity_days: 90,
  includes: [] as string[],
  sort_order: 0,
});

export default function PackageTemplatesEditor() {
  const { showToast } = useToast();
  const [packages, setPackages] = useState<CoachingPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(makeEmptyForm());
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [newInclude, setNewInclude] = useState('');

  // Zugewiesene Pakete (alle Klienten)
  const [allClientPkgs, setAllClientPkgs] = useState<any[]>([]);
  const [clientPkgsLoading, setClientPkgsLoading] = useState(false);

  const loadPackages = useCallback(async () => {
    try {
      const res = await packagesApi.list({ active_only: true });
      setPackages(res.data);
    } catch (err) {
      showToast('Fehler beim Laden der Pakete', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadClientPackages = useCallback(async () => {
    setClientPkgsLoading(true);
    try {
      const res = await packagesApi.clientPackages({});
      setAllClientPkgs(res.data);
    } catch { /* ignore */ }
    finally { setClientPkgsLoading(false); }
  }, []);

  useEffect(() => { loadPackages(); loadClientPackages(); }, [loadPackages, loadClientPackages]);

  // ESC-Key zum Schließen des Modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showEditor) setShowEditor(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showEditor]);

  // Body-Scroll blockieren wenn Modal offen
  useEffect(() => {
    if (showEditor) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showEditor]);

  const handleEdit = (pkg: CoachingPackage) => {
    setEditingId(pkg.id);
    setForm({
      name: pkg.name,
      description: pkg.description || '',
      session_count: pkg.session_count,
      session_duration_min: pkg.session_duration_min,
      price_eur: pkg.price_eur,
      category: pkg.category || 'Leadership',
      tier: pkg.tier || 'fokus',
      format: pkg.format || '1:1',
      max_participants: pkg.max_participants,
      validity_days: pkg.validity_days || 90,
      includes: pkg.includes ? [...pkg.includes] : [],
      sort_order: pkg.sort_order,
    });
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingId(null);
    setForm(makeEmptyForm());
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast('Bitte einen Namen eingeben', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await packagesApi.update(editingId, form);
        showToast('Paket aktualisiert', 'success');
      } else {
        await packagesApi.create(form);
        showToast('Paket erstellt', 'success');
      }
      setShowEditor(false);
      await loadPackages();
    } catch (err) {
      showToast('Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    if (!window.confirm(`"${name}" wirklich löschen?`)) return;
    setActionLoading(id);
    try {
      await packagesApi.delete(id);
      showToast('Paket gelöscht', 'success');
      await loadPackages();
    } catch (err) {
      showToast('Fehler beim Löschen', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setActionLoading(id);
    try {
      await packagesApi.duplicate(id);
      showToast('Paket dupliziert', 'success');
      await loadPackages();
    } catch (err) {
      showToast('Fehler beim Duplizieren', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('12 AC Leadership Standard-Vorlagen erstellen?')) return;
    setLoading(true);
    try {
      await packagesApi.seedDefaults();
      showToast('12 Standardvorlagen erstellt!', 'success');
      await loadPackages();
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Fehler beim Erstellen';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addInclude = () => {
    if (newInclude.trim()) {
      setForm({ ...form, includes: [...form.includes, newInclude.trim()] });
      setNewInclude('');
    }
  };

  const removeInclude = (index: number) => {
    setForm({ ...form, includes: form.includes.filter((_, i) => i !== index) });
  };

  const pricePerSession = form.session_count > 0 ? form.price_eur / form.session_count : 0;

  const filteredPackages = filterCategory
    ? packages.filter(p => p.category === filterCategory)
    : packages;

  const groupedPackages: Record<string, CoachingPackage[]> = {};
  filteredPackages.forEach(pkg => {
    const cat = pkg.category || 'Sonstige';
    if (!groupedPackages[cat]) groupedPackages[cat] = [];
    groupedPackages[cat].push(pkg);
  });

  if (loading) return <div className="card" style={{ padding: 32 }}>Lädt Pakete...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <p className="text-muted text-sm">{packages.length} Paketvorlagen</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {packages.length === 0 && (
            <button className="btn btn-secondary" onClick={handleSeedDefaults}>
              <Sparkles size={16} /> Standard-Vorlagen laden
            </button>
          )}
          <button className="btn btn-primary" onClick={handleCreate}>
            <Plus size={16} /> Neues Paket
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="category-chips mb-24">
        <button
          className={`chip ${!filterCategory ? 'chip-active' : ''}`}
          onClick={() => setFilterCategory('')}
        >
          Alle
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`chip ${filterCategory === cat ? 'chip-active' : ''}`}
            onClick={() => setFilterCategory(filterCategory === cat ? '' : cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Pakete nach Kategorie */}
      {Object.keys(groupedPackages).length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <Package size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
          <h3>Noch keine Paketvorlagen</h3>
          <p className="text-muted">Erstelle deine ersten Pakete oder lade die 12 AC Leadership Standardvorlagen.</p>
          <button className="btn btn-primary" onClick={handleSeedDefaults} style={{ marginTop: 16 }}>
            <Sparkles size={16} /> Standard-Vorlagen laden
          </button>
        </div>
      ) : (
        Object.entries(groupedPackages).map(([category, pkgs]) => (
          <div key={category} style={{ marginBottom: 24 }}>
            <h3 style={{
              fontSize: '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: CATEGORY_COLORS[category] || 'var(--text-muted)',
              marginBottom: 12,
              borderBottom: `2px solid ${CATEGORY_COLORS[category] || '#ddd'}`,
              paddingBottom: 6,
            }}>
              {category}
            </h3>
            <div style={{ display: 'grid', gap: 12 }}>
              {pkgs.map(pkg => (
                <div key={pkg.id} className="card" style={{
                  padding: '16px 20px',
                  opacity: actionLoading === pkg.id ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <h4 style={{ margin: 0 }}>{pkg.name}</h4>
                        {pkg.is_system_template && (
                          <span style={{
                            fontSize: '0.65rem', background: 'var(--success-bg, #e8f5e9)', color: 'var(--success-text, #2e7d32)',
                            padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                          }}>
                            STANDARD
                          </span>
                        )}
                        {pkg.tier && (
                          <span style={{
                            fontSize: '0.65rem',
                            background: pkg.tier === 'transformation' ? '#fff3e0' : '#e3f2fd',
                            color: pkg.tier === 'transformation' ? '#e65100' : '#1565c0',
                            padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                          }}>
                            {pkg.tier === 'transformation' ? 'TRANSFORMATION' : 'FOKUS'}
                          </span>
                        )}
                      </div>
                      {pkg.description && (
                        <p className="text-muted text-sm" style={{ margin: '4px 0 8px' }}>{pkg.description}</p>
                      )}
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={14} /> {pkg.session_count}&times; {pkg.session_duration_min} Min
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Euro size={14} /> <strong>{pkg.price_eur.toFixed(0)} &euro;</strong>
                          {pkg.price_per_session != null && pkg.price_per_session > 0 && (
                            <span className="text-muted">({pkg.price_per_session.toFixed(0)} &euro;/Sitzung)</span>
                          )}
                        </span>
                        {pkg.format && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Users size={14} /> {pkg.format}
                            {pkg.max_participants != null && ` (max. ${pkg.max_participants})`}
                          </span>
                        )}
                        {pkg.validity_days != null && pkg.validity_days > 0 && (
                          <span className="text-muted">{pkg.validity_days} Tage gültig</span>
                        )}
                      </div>
                      {pkg.includes && pkg.includes.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {pkg.includes.map((inc, i) => (
                            <span key={i} style={{
                              fontSize: '0.75rem', background: 'var(--bg)', padding: '2px 8px',
                              borderRadius: 12, color: 'var(--text-muted)',
                            }}>
                              {inc}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 16, flexShrink: 0, alignItems: 'center' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Bearbeiten"
                        disabled={actionLoading === pkg.id}
                        onClick={(e) => { e.stopPropagation(); handleEdit(pkg); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}
                      >
                        <Edit3 size={14} /> Bearbeiten
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Duplizieren"
                        disabled={actionLoading === pkg.id}
                        onClick={(e) => handleDuplicate(e, pkg.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Löschen"
                        disabled={actionLoading === pkg.id}
                        onClick={(e) => handleDelete(e, pkg.id, pkg.name)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--danger)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Zugewiesene Pakete Uebersicht */}
      <div className="card" style={{ padding: 24, marginTop: 32 }}>
        <h3 style={{ margin: '0 0 4px' }}>Zugewiesene Pakete</h3>
        <p className="text-muted text-sm" style={{ margin: '0 0 16px' }}>Alle deinen Klienten zugewiesenen Coaching-Pakete.</p>

        {(() => {
          const active = allClientPkgs.filter((cp: any) => cp.status === 'active');
          const completed = allClientPkgs.filter((cp: any) => cp.status === 'completed');
          const expired = allClientPkgs.filter((cp: any) => cp.status === 'expired');
          const totalRevenue = allClientPkgs.reduce((sum: number, cp: any) => sum + (cp.price_paid_eur || 0), 0);
          const totalUsed = allClientPkgs.reduce((sum: number, cp: any) => sum + (cp.sessions_used || 0), 0);
          const totalTotal = allClientPkgs.reduce((sum: number, cp: any) => sum + (cp.sessions_total || 0), 0);

          return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, textAlign: 'center' }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Aktiv</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: '4px 0 0', color: 'var(--success-text, #2e7d32)' }}>{active.length}</p>
                </div>
                <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, textAlign: 'center' }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Abgeschlossen</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: '4px 0 0', color: 'var(--primary)' }}>{completed.length}</p>
                </div>
                <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, textAlign: 'center' }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Abgelaufen</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: '4px 0 0', color: 'var(--danger-text, #991b1b)' }}>{expired.length}</p>
                </div>
                <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, textAlign: 'center' }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Umsatz</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: '4px 0 0' }}>{totalRevenue.toFixed(0)} EUR</p>
                </div>
                <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, textAlign: 'center' }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Sitzungen</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: 700, margin: '4px 0 0' }}>{totalUsed} / {totalTotal}</p>
                </div>
              </div>

              {clientPkgsLoading ? (
                <div style={{ padding: 16, textAlign: 'center' }}>
                  <p className="text-muted">Lade zugewiesene Pakete...</p>
                </div>
              ) : allClientPkgs.length === 0 ? (
                <p className="text-muted" style={{ textAlign: 'center', padding: 16 }}>
                  Noch keine Pakete zugewiesen. Weise Pakete im Klienten-Profil unter dem Tab "Pakete" zu.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {allClientPkgs.map((cp: any) => {
                    const pct = cp.sessions_total > 0 ? Math.round((cp.sessions_used / cp.sessions_total) * 100) : 0;
                    const remainPct = cp.sessions_total > 0 ? (cp.sessions_remaining / cp.sessions_total) * 100 : 0;
                    const barColor = cp.status !== 'active' ? 'var(--text-muted)' : remainPct > 50 ? 'var(--success-text, #2e7d32)' : remainPct > 20 ? '#B8860B' : 'var(--danger, #dc2626)';
                    const statusColor = cp.status === 'active' ? 'var(--success-text, #2e7d32)' : cp.status === 'completed' ? 'var(--primary)' : 'var(--danger-text, #991b1b)';
                    const statusLabels: Record<string, string> = { active: 'Aktiv', completed: 'Abgeschlossen', expired: 'Abgelaufen' };
                    return (
                      <div key={cp.id} style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div>
                            <strong style={{ fontSize: '0.9rem' }}>{cp.client_name || 'Klient'}</strong>
                            <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{cp.package_name}</span>
                          </div>
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: statusColor, background: `${statusColor}15`, padding: '2px 8px', borderRadius: 4 }}>
                            {statusLabels[cp.status] || cp.status}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
                          </div>
                          <span className="text-sm text-muted" style={{ minWidth: 90, textAlign: 'right', fontSize: '0.78rem' }}>
                            {cp.sessions_used}/{cp.sessions_total} Sitzungen
                          </span>
                        </div>
                        <div className="text-muted" style={{ display: 'flex', gap: 14, fontSize: '0.75rem', marginTop: 4 }}>
                          <span>{cp.price_paid_eur?.toFixed(2)} EUR</span>
                          {cp.sessions_total && cp.price_paid_eur ? <span>({(cp.price_paid_eur / cp.sessions_total).toFixed(2)} EUR/Sitzung)</span> : null}
                          {cp.valid_until && <span>Gültig bis: {new Date(cp.valid_until).toLocaleDateString('de-DE')}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEditor(false); }}
        >
          <div className="card" style={{
            width: 560, maxHeight: '90vh', overflowY: 'auto', padding: 32,
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>{editingId ? 'Paket bearbeiten' : 'Neues Paket'}</h2>
              <button className="btn btn-icon" onClick={() => setShowEditor(false)}><X size={20} /></button>
            </div>

            <div className="form-group">
              <label>Paketname *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="z.B. Leadership Fokus Basis" />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Kurze Beschreibung des Pakets" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kategorie</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Stufe</label>
                <select value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })}>
                  {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Format</label>
                <select value={form.format} onChange={e => setForm({ ...form, format: e.target.value })}>
                  {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Max. Teilnehmer</label>
                <input type="number" value={form.max_participants ?? ''} onChange={e => setForm({ ...form, max_participants: e.target.value ? parseInt(e.target.value) : null })} placeholder="Optional" min={1} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Anzahl Sitzungen</label>
                <input type="number" value={form.session_count} onChange={e => setForm({ ...form, session_count: parseInt(e.target.value) || 1 })} min={1} />
              </div>
              <div className="form-group">
                <label>Dauer pro Sitzung (Min)</label>
                <input type="number" value={form.session_duration_min} onChange={e => setForm({ ...form, session_duration_min: parseInt(e.target.value) || 60 })} min={15} step={15} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Gesamtpreis (EUR)</label>
                <input type="number" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: parseFloat(e.target.value) || 0 })} min={0} step={10} />
              </div>
              <div className="form-group">
                <label>Preis pro Sitzung</label>
                <input type="text" value={`${pricePerSession.toFixed(2)} \u20AC`} readOnly style={{ background: 'var(--bg)', color: 'var(--text-muted)' }} />
              </div>
            </div>

            <div className="form-group">
              <label>Gültigkeit (Tage)</label>
              <input type="number" value={form.validity_days} onChange={e => setForm({ ...form, validity_days: parseInt(e.target.value) || 90 })} min={1} style={{ maxWidth: 150 }} />
            </div>

            <div className="form-group">
              <label>Inklusive Leistungen</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={newInclude} onChange={e => setNewInclude(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInclude(); } }}
                  placeholder="z.B. Vorgespräch" style={{ flex: 1 }} />
                <button className="btn btn-secondary" onClick={addInclude} type="button">
                  <Plus size={16} />
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.includes.map((inc, i) => (
                  <span key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.8rem', background: 'var(--bg)', padding: '4px 10px',
                    borderRadius: 12, cursor: 'pointer',
                  }} onClick={() => removeInclude(i)}>
                    {inc} <X size={12} />
                  </span>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Sortierung</label>
              <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} min={0} style={{ maxWidth: 100 }} />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 1 }}>
                <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowEditor(false)} style={{ flex: 1 }}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
